import { CheckCircle2, Download } from 'lucide-react';
import { Button } from '@components/ui/button';
import { SectionBadge } from './SectionBadge';
import { formatWeight } from '@generated/utils/units';
import { formatCurrencyWhole, formatShortDate } from '@generated/utils/format';
import { periodCostLabel, annualCostLabel } from '@generated/utils/pricing';
import { triggerBrowserDownload } from '@generated/utils/reservation-pdf';
import type { CompanyInfo, Commitment, FormatItem, Frequency, MaterialItem, MaterialSelection, Transportation, Unit } from '@generated/types';

interface ConfirmationViewProps {
  referenceNumber: string;
  submittedAt: Date;
  company: CompanyInfo;
  materials: MaterialItem[];
  selections: Record<string, MaterialSelection>;
  unit: Unit;
  totalLbs: number;
  formats: FormatItem[];
  formatId: string | null;
  frequency: Frequency | null;
  transportation: Transportation | null;
  desiredShipmentDate: Date | undefined;
  commitment: Commitment | null;
  shipmentSizeSentence: string | null;
  periodCost: number | null;
  annualCost: number | null;
  costBasisNote: string | null;
  signatoryName: string;
  signatoryTitle: string;
  signatureDate: Date | null;
  pdfBlob: Blob | null;
  pdfFileName: string | null;
  onReset: () => void;
}

export const FREQUENCY_LABEL: Record<Frequency, string> = {
  Monthly: 'Monthly',
  Quarterly: 'Quarterly',
  Annual: 'One-Time / Annually',
};
export const TRANSPORT_LABEL: Record<Transportation, string> = {
  'Partner arranges': 'Customer-Arranged Transportation',
  'ByFusion arranges': 'ByFusion to arrange transportation',
};
export const COMMITMENT_LABEL: Record<Commitment, string> = {
  'Month-to-Month': 'Month-to-month',
  '6 months': '6 months',
  '12 months': '12 months',
  '24 months': '24 months',
  '36 months': '36 months',
  '48 months': '48 months',
};

export function ConfirmationView(props: ConfirmationViewProps) {
  const {
    referenceNumber,
    submittedAt,
    company,
    materials,
    selections,
    unit,
    totalLbs,
    formats,
    formatId,
    frequency,
    transportation,
    desiredShipmentDate,
    commitment,
    shipmentSizeSentence,
    periodCost,
    annualCost,
    signatoryName,
    signatoryTitle,
    signatureDate,
    pdfBlob,
    pdfFileName,
    onReset,
  } = props;

  const selectedFormat = formats.find((f) => f.id === formatId);
  const selectedMaterials = materials.filter((m) => selections[m.id]?.checked);
  const isHandling = selectedFormat?.handlingPerUnit != null;
  const showPeriodCost = !!frequency && frequency !== 'Annual';

  function handleDownload() {
    if (!pdfBlob || !pdfFileName) return;
    triggerBrowserDownload(pdfBlob, pdfFileName);
  }

  return (
    <div className="mx-auto max-w-[900px] px-4 pb-16 pt-8 sm:px-6">
      <style>{`
        @media print {
          #receipt-panel { box-shadow: none !important; }
        }
      `}</style>

      <div className="flex flex-col items-center text-center print:hidden">
        <span className="flex h-14 w-14 items-center justify-center rounded-full bg-[hsl(var(--success)/0.12)] text-[hsl(var(--success))]">
          <CheckCircle2 className="h-7 w-7" />
        </span>
        <h1 className="mt-4 font-[family-name:var(--font-heading)] text-2xl font-medium text-foreground">
          Reservation Submitted
        </h1>
        <p className="mt-1 text-sm text-muted-foreground">Thank you! Your capacity reservation has been received.</p>

        {pdfFileName ? (
          <p className="mt-5 text-xs text-muted-foreground">
            Your reservation PDF is ready as{' '}
            <span className="font-medium text-foreground">{pdfFileName}</span>.
          </p>
        ) : null}

        <div className="mt-3 flex items-center justify-center">
          <Button onClick={handleDownload} disabled={!pdfBlob}>
            <Download className="h-4 w-4" />
            Download Reservation PDF
          </Button>
        </div>
      </div>

      <div id="receipt-panel" className="mt-8 overflow-hidden rounded-2xl border border-border bg-card shadow-[var(--shadow-stack)] print:border-border print:bg-transparent print:shadow-none">
        <div className="flex items-start justify-between gap-4 border-b border-border p-6">
          <div>
            <img
              src="https://files-public.monday.com/use1/90515cd5-3bf5-42c7-b8e8-9206f988e523/BYFUSION-LOGO-RGB-01_1080.png"
              alt="ByFusion"
              className="h-6 w-auto object-contain"
            />
            <h2 className="mt-3 font-[family-name:var(--font-heading)] text-lg font-medium text-foreground">
              Capacity Reservation Request
            </h2>
            <p className="text-sm text-muted-foreground">ByFusion Global, Inc.</p>
          </div>
          <div className="tnum text-right">
            <div className="text-xs text-muted-foreground">Reference Number</div>
            <div className="text-sm font-semibold text-primary">{referenceNumber}</div>
          </div>
        </div>

        <div className="border-b border-border p-6">
          <h3 className="text-sm font-medium text-foreground">Company Information</h3>
          <div className="mt-3 grid grid-cols-1 gap-4 text-sm md:grid-cols-2">
            <Field label="Company Name" value={company.company} />
            <Field label="Contact Name" value={company.contactName} />
            <Field label="Email" value={company.email} />
            <Field label="Phone" value={company.phone} />
            <div className="md:col-span-2">
              <Field label="Shipping Origin" value={company.shippingOrigin} />
            </div>
          </div>
        </div>

        <div className="border-b border-border p-6">
          <h3 className="text-sm font-medium text-foreground">Material Details</h3>
          <div className="mt-3 divide-y divide-border">
            {selectedMaterials.map((m) => (
              <div key={m.id} className="flex items-center justify-between py-2 text-sm">
                <span className="text-muted-foreground">{m.name}</span>
                <span className="tnum text-foreground">
                  {formatWeight(selections[m.id]?.weightLbs ?? 0, unit)} {unit}
                </span>
              </div>
            ))}
          </div>
          <div className="mt-2 flex items-center justify-between border-t border-border pt-3">
            <span className="text-sm font-medium text-foreground">Total Monthly Volume</span>
            <span className="tnum text-sm font-medium text-foreground">{formatWeight(totalLbs, 'lbs')} lbs</span>
          </div>
        </div>

        <div className="border-b border-border p-6">
          <h3 className="text-sm font-medium text-foreground">Logistics &amp; Shipping Method</h3>
          <div className="mt-3 grid grid-cols-1 gap-4 text-sm md:grid-cols-2">
            <Field label="Shipping Method" value={selectedFormat?.name ?? ''} />
            <Field label="Shipping Frequency" value={frequency ? FREQUENCY_LABEL[frequency] : ''} />
            <Field label="Transportation Coordination" value={transportation ? TRANSPORT_LABEL[transportation] : ''} />
            <Field label="Target First Shipment Date" value={formatShortDate(desiredShipmentDate)} />
            <Field label="Commitment Length" value={commitment ? COMMITMENT_LABEL[commitment] : ''} />
            <Field label="Estimated Shipment Size" value={shipmentSizeSentence ?? ''} />
          </div>
        </div>

        <div className="border-b border-border p-6">
          <h3 className="text-sm font-medium text-foreground">Cost Estimates</h3>
          <div className="tnum mt-3 grid grid-cols-1 gap-4 text-sm md:grid-cols-2">
            {showPeriodCost ? (
              <Field label={periodCostLabel(frequency, isHandling) ?? 'Estimated cost'} value={formatCurrencyWhole(periodCost)} />
            ) : null}
            <Field label={annualCostLabel(isHandling)} value={formatCurrencyWhole(annualCost)} />
          </div>
        </div>

        <div className="border-b border-border p-6">
          <h3 className="text-sm font-medium text-foreground">Authorization</h3>
          <div className="mt-3 grid grid-cols-1 gap-4 text-sm md:grid-cols-2">
            <Field label="Signatory Name" value={signatoryName} />
            <Field label="Signatory Title" value={signatoryTitle} />
            <Field label="Signature Date" value={formatShortDate(signatureDate)} />
            <Field label="Authorization Confirmed" value="Yes" />
          </div>
        </div>

        <div className="flex items-center justify-between border-b border-border p-6">
          <div>
            <div className="text-xs text-muted-foreground">Status</div>
            <span className="mt-1.5 inline-flex items-center rounded-full border border-[hsl(var(--success)/0.35)] bg-[hsl(var(--success)/0.12)] px-2.5 py-0.5 text-xs font-medium text-[hsl(var(--success))]">
              Received
            </span>
          </div>
          <div className="tnum text-right">
            <div className="text-xs text-muted-foreground">Submission Date</div>
            <div className="text-sm text-foreground">{formatShortDate(submittedAt)}</div>
          </div>
        </div>

        <div className="border-b border-border p-6">
          <h3 className="text-sm font-medium text-foreground">What Happens Next</h3>
          <div className="mt-4 space-y-4">
            <Step
              number={1}
              title="We review your submission"
              description="Our operations team will confirm material fit and available capacity within 2 business days."
            />
            <Step
              number={2}
              title="You secure your reservation"
              description="Once approved, submit your deposit to lock in your reserved capacity."
            />
            <Step
              number={3}
              title="We finalize your agreement"
              description="We'll email your fully executed reservation, followed by the formal service agreement with final rates and transportation details."
            />
          </div>
        </div>

        <div className="border-b border-border bg-secondary/60 p-6 print:bg-transparent">
          <h3 className="text-sm font-medium text-foreground">Questions?</h3>
          <p className="mt-1 text-sm text-muted-foreground">Our operations team is here to help. Reach out anytime.</p>
          <div className="mt-3 space-y-1 text-sm text-foreground">
            <div>operations@byfusion.com</div>
            <div>+1 833 BYBLOCK</div>
            <div>byfusion.com</div>
          </div>
        </div>

        <div className="p-6 text-center text-xs text-muted-foreground">
          <p>ByFusion Global, Inc. · Confidential between the parties</p>
          <p className="mx-auto mt-2 max-w-[560px]">
            This reservation is non-binding and does not constitute a purchase order or guarantee capacity, pricing, or
            delivery timing. Final terms will be established through a separate written agreement.
          </p>
        </div>
      </div>

      <div className="mt-6 flex justify-center print:hidden">
        <button onClick={onReset} className="text-sm font-medium text-primary underline-offset-2 hover:underline">
          Submit another reservation
        </button>
      </div>
    </div>
  );
}

function Field({ label, value }: { label: string; value: string }) {
  const display = value && value.trim().length > 0 ? value : '\u2014';
  return (
    <div>
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className="mt-0.5 text-sm text-foreground">{display}</div>
    </div>
  );
}

function Step({ number, title, description }: { number: number; title: string; description: string }) {
  return (
    <div className="flex items-start gap-3">
      <SectionBadge number={number} />
      <div>
        <div className="text-sm font-medium text-foreground">{title}</div>
        <div className="mt-0.5 text-xs text-muted-foreground">{description}</div>
      </div>
    </div>
  );
}
